import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, AlertTriangle } from "lucide-react";
import { api, withReason } from "@/lib/api";

const ENV_BADGE = { live: "b-healthy", demo: "b-wait" };

export default function PlatformTenants() {
  const [tenants, setTenants] = useState(null);
  const [busy, setBusy] = useState({});
  const [msg, setMsg] = useState("");
  const nav = useNavigate();

  const load = () =>
    api.get("/platform/tenants").then((r) => setTenants(r.data.tenants || [])).catch(() => setTenants([]));

  useEffect(() => { load(); }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 5000); };

  const setStatus = async (t, action) => {
    const verb = action === "suspend" ? "Suspend" : "Reactivate";
    // Suspension locks every member of a real company out of their workspace
    // on their next request. That deserves a sentence, not a spinner.
    const reason = window.prompt(
      `${verb} ${t.name}?\n\n` +
      (action === "suspend"
        ? "Every member is locked out on their next request. Nothing is deleted and reactivating restores the workspace exactly.\n\n"
        : "Members regain access immediately.\n\n") +
      "Why? (recorded in the staff audit trail)"
    );
    if (reason === null) return;
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      await api.post(`/platform/tenants/${t.id}/${action}`, {}, withReason(reason));
      await load();
      flash(`${t.name} ${action === "suspend" ? "suspended" : "reactivated"}.`);
    } catch (e) {
      flash(e?.response?.data?.message || `Could not ${action} ${t.name}`);
    } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
  };

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>
        Platform
      </div>
      <h1 className="font-display text-[26px]">Client workspaces</h1>
      <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
        Every workspace Prioris runs. Counts only — a workspace's cases and customers stay inside it.
      </p>

      {msg && <div className="badge b-healthy mt-4 inline-block" data-testid="tenants-msg">{msg}</div>}

      <div className="card overflow-x-auto mt-5" data-testid="platform-tenants">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Workspace</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Env</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Plan</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Members</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Connectors</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Cases</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {tenants === null && (
              <tr><td colSpan={8} className="py-10 text-center" style={{ color: "var(--text-muted)" }}>Loading workspaces…</td></tr>
            )}
            {tenants?.length === 0 && (
              <tr><td colSpan={8} className="py-12 text-center">
                <Building2 size={22} className="mx-auto mb-2" color="var(--text-muted)" />
                <div style={{ color: "var(--text-muted)" }}>No workspaces yet. Onboard your first client.</div>
              </td></tr>
            )}
            {tenants?.map((t) => (
              <tr key={t.id} className="table-row cursor-pointer"
                  style={{ borderBottom: "1px solid var(--border)" }}
                  data-testid={`platform-tenant-${t.slug}`}
                  onClick={() => nav(`/admin/tenants/${t.id}`)}>
                <td className="py-2.5 px-3">
                  <div>{t.name}</div>
                  <div className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>{t.slug}</div>
                </td>
                <td className="py-2.5 px-3">
                  <span className={`badge ${ENV_BADGE[t.environment] || "b-muted"}`}>{t.environment}</span>
                </td>
                <td className="py-2.5 px-3">
                  {/* No subscription row is a real state: the workspace falls
                      back to the smallest plan without anyone deciding that. */}
                  {t.plan_code
                    ? <span className="font-mono text-[12px]">
                        {t.plan_code}{t.billing_term && ` · ${t.billing_term.replace(/_/g, " ")}`}
                      </span>
                    : <span className="badge b-sla" title="No subscription — silently on the fallback plan">unprovisioned</span>}
                </td>
                <td className="py-2.5 px-3 font-mono">{t.member_count ?? 0}</td>
                <td className="py-2.5 px-3 font-mono">
                  {t.connector_count ?? 0}
                  {t.connector_failing_count > 0 && (
                    <span className="badge b-rev ml-2" title="Connectors in an error state">
                      <AlertTriangle size={10} className="inline mr-1" />{t.connector_failing_count}
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-3 font-mono">{t.case_count ?? 0}</td>
                <td className="py-2.5 px-3">
                  <span className={`badge ${t.status === "active" ? "b-healthy" : "b-rev"}`}>{t.status}</span>
                </td>
                <td className="py-2.5 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn btn-ghost"
                    disabled={busy[t.id]}
                    data-testid={`toggle-status-${t.slug}`}
                    onClick={() => setStatus(t, t.status === "active" ? "suspend" : "activate")}
                  >
                    {t.status === "active" ? "Suspend" : "Reactivate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
